import moment from 'moment';
import xlsx from 'xlsx';
import ExcelJS from 'exceljs';
import {
  aggregateUrlStringMap,
  optionalColumnsForJlr,
  optionalColumns,
  filterOptions,
  filterOptionsForJlr,
  filterOptionsForPV,
  optionalColumnsExceptCv,
} from '../staticData';
import {
  DATE_FORMAT,
  DISPLAY_MESSAGES,
  ENV_TYPES,
  JLR_PLANT,
  APPLICATION_VARIANTS,
  headerForExcel,
  mes4HeaderForExcel,
  headerForAgsnExcel,
  sheet2Data,
  API_RESOURCE_URLS,
  headerForCabAgsnExcel,
  headerForEvrExcel,
  INTERVAL_TYPE,
  AGGREGATES,
  PANTNAGAR_PLANT_NAME,
  USER_OPERATIONS,
} from '../constants';
import config from '../config/config';
import { API } from '../apis/api';

const { APPLICATION_VARIANT } = config;

/**
 * Checks if the given plant is the Pantnagar plant.
 * @param {Object} plant - The plant object with a `label` property.
 * @returns {boolean} True if the plant is Pantnagar.
 */
export const isPantnagarPlant = (plant) => {
  return plant?.label === PANTNAGAR_PLANT_NAME;
};

/**
 * Returns the appropriate auth operation for aggregate screens based on the plant.
 * For Pantnagar plant, returns GET_AGGREGATE_FOR_UTK ('central-ppc-aggregate')
 * so users with that role can access aggregate operations.
 * For all other plants, returns the provided defaultOperation unchanged.
 *
 * @param {string} defaultOperation - The default USER_OPERATIONS value for non-Pantnagar plants.
 * @param {Object} plant - The plant object with a `label` property.
 * @returns {string} The resolved auth operation string.
 */
export const getAggregateAuthOperation = (defaultOperation, plant) => {
  if (isPantnagarPlant(plant)) {
    return USER_OPERATIONS.GET_AGGREGATE_FOR_UTK;
  }
  return defaultOperation;
};

/**
 * Checks if the current user is a UTK-only user at Pantnagar plant.
 * Returns true ONLY when ALL 3 conditions are met:
 *   1. Plant is Pantnagar
 *   2. User has GET_AGGREGATE_FOR_UTK operation (UTK role)
 *   3. User does NOT have CREATE_AGGREGATE_PLAN_SEQUENCE operation
 *
 * Admin users at Pantnagar (who have BOTH operations) will return false,
 * so Upload and Add Emergency AgSn remain enabled for them.
 *
 * @param {Object} plant - The plant object with a `label` property.
 * @param {string[]} allowedOperations - The user's allowed operations from auth context.
 * @returns {boolean} True if the user is a UTK-only user at Pantnagar.
 */
export const isPantnagarUtkOnlyUser = (plant, allowedOperations) => {
  if (!isPantnagarPlant(plant)) return false;

  const hasUtkRole = allowedOperations?.includes(USER_OPERATIONS.GET_AGGREGATE_FOR_UTK);
  const hasFullAggregateAccess = allowedOperations?.includes(USER_OPERATIONS.CREATE_AGGREGATE_PLAN_SEQUENCE);

  // Disable only when user has UTK role but NOT the full aggregate access
  return hasUtkRole && !hasFullAggregateAccess;
};

export const filterRows = (initialRows, filters) => {
  let rows = initialRows;
  Object.keys(filters).forEach(filterField => {
    rows = rows.filter(row => {
      const { plan, attributes, ...rest } = row;
      const currentRow = { ...rest, ...plan, ...attributes };
      const filter = filters[filterField];
      const value = currentRow[filterField];
      if (
        filter.selectedFilterParameters.size === 0 ||
        filter.selectedFilterParameters.has(value)
      )
        return true;
      return false;
    });
  });
  return rows;
};

export const isPositiveIntegerOrZero = valueString => {
  const regex = /^[0-9]{1,6}$/;
  return regex.test(valueString);
};
export const isPositiveInteger = valueString => {
  const isAPositiveIntegerIntOrZero = isPositiveIntegerOrZero(valueString);
  return isAPositiveIntegerIntOrZero && Number(valueString) > 0;
};
export const isQuantityValid = valueString => {
  const isQuantityValidorNot = valueString >= 0 && valueString <= 499;
  return isQuantityValidorNot;
};
export const isEvrNumberValid = valueString => {
  const isAlphanumericWithValidLength = /^[a-zA-Z0-9]{1,15}$/.test(valueString);
  const isNotNullString = !/null/i.test(valueString); // Checks if the string is not "null" in any case
  return isAlphanumericWithValidLength && isNotNullString;
};

export const getErrorMessage = err => {
  if (err.response) {
    if (err.response.data && err.response.data.error) {
      return err.response.data.error;
    } else if (err.response.data && err.response.data.errors) {
      return err.response.data.errors;
    }
    return err.response.data;
  }
  return err.message;
};

export const getDatesForRange = (startDate, endDate) => {
  const startDateCopy = moment(startDate, DATE_FORMAT.ISO);
  const endDateCopy = moment(endDate, DATE_FORMAT.ISO);

  const dates = [];
  while (startDateCopy.isSameOrBefore(endDateCopy, 'day')) {
    dates.push(moment(startDateCopy));
    startDateCopy.add(1, 'days');
  }
  return dates;
};

export const isSunday = date => date.day() === 0;

export const getAggregateUrl = aggregate =>
  `/aggregate/${aggregateUrlStringMap[aggregate]}`;

export const getMastereUrl = aggregate =>
  `/aggregate/${aggregateUrlStringMap[aggregate]}`;

export const getAggregateDispatchUrl = aggregate =>
  `/aggregate/dispatch/${aggregateUrlStringMap[aggregate]}`;

export const getAggregateAvailabilityUrl = aggregate =>
  `/aggregate/Availability/${aggregateUrlStringMap[aggregate]}`;

export const getAggregateLineUpUrl = aggregate =>
  `/aggregate/agsns/${aggregateUrlStringMap[aggregate]}`;

export const getAggregatePlanUrl = aggregate =>
  `/aggregate/plan/${aggregateUrlStringMap[aggregate]}`;

export const getAggregateReSequenceUrl = aggregate =>
  `/aggregate/resequence/${aggregateUrlStringMap[aggregate]}`;

export const getAggregateSequenceUrl = aggregate =>
  `/aggregate/sequence/${aggregateUrlStringMap[aggregate]}`;

export const getEmergencyAgSNUrl = aggregate =>
  `/aggregate/emergency-agsn/${aggregateUrlStringMap[aggregate]}`;

export const getAgSNOverviewUrl = aggregate =>
  `/agsn-overview/${aggregateUrlStringMap[aggregate]}`;

export const getInterplantAggregateAssignment = aggregate =>
  `/aggregate/interplant-aggregate-assignment/${aggregateUrlStringMap[aggregate]}`;

export const getFreePoolAggregateUrl = aggregate =>
  `/aggregate/free-pool/${aggregateUrlStringMap[aggregate]}`;

export const convertToTitleCase = string => {
  const words = string.split(' ');
  const capitalizedWords = words.map(
    word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
  );
  return capitalizedWords.join(' ');
};

export const callFunctionConditionallyWithWarningCurry = (
  func,
  conditionToWarnFor,
) => (...params) => {
  let shouldCallFunction = true;
  if (conditionToWarnFor) {
    shouldCallFunction = window.confirm(DISPLAY_MESSAGES.NAVIGATION_WARNING);
  }
  if (shouldCallFunction) return func(...params) || true;
  return false;
};

export const initializeScript = src =>
  new Promise(function (resolve, reject) {
    var script = document.createElement('script');
    script.src = src;
    script.onload = () => resolve();
    script.onError = e => reject(e);
    document.body.appendChild(script);
  });

export const logoutAndClearData = keycloak => {
  return keycloak.logout().then(() => {
    window.keycloak = null;
    window.location.href = '/';
  });
};

export const debugLog = (...args) =>
  config.ENV_TYPE === ENV_TYPES.TEST && console.log(...args);

export const getISTTime = unixTimeInSeconds =>
  moment.unix(unixTimeInSeconds).format('hh:mm:ssa');

export const downloadCsv = (csv, exportedFilename) => {
  var blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  var url = URL.createObjectURL(blob);
  var link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', exportedFilename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  console.info(exportedFilename + ' downloaded successfully');
};

export const getMonthNameWithYear = timestampInMoment => {
  // for e.g. Jan-2021
  return `${timestampInMoment.format('MMM')}-${timestampInMoment.format(
    'YYYY',
  )}`;
};

export const getMonthForStartDate = startDate =>
  startDate?.format?.('MMMM') || ' - - ';

export const handleDownloadTemplate = fileName => {
  const link = document.createElement('a');
  link.href = `/${fileName}`;
  link.setAttribute('download', `${fileName}`);
  document.body.appendChild(link);
  link.click();
  link.parentNode.removeChild(link);
};

export const readExcel = (file, setFileData, csvDataFormatter) => {
  const promise = new Promise((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.readAsArrayBuffer(file);

    fileReader.onload = e => {
      const bufferArray = e.target.result;
      const workBook = xlsx.read(bufferArray, { type: 'buffer' });
      const worksheetName = workBook.SheetNames[0];
      const workSheet = workBook.Sheets[worksheetName];
      const data = xlsx.utils.sheet_to_json(workSheet, { defval: '' });
      if (csvDataFormatter) {
        const updatedData = csvDataFormatter(data);
        resolve(updatedData);
      } else {
        resolve(data);
      }
    };

    fileReader.onerror = error => {
      reject(error);
    };
  });
  promise.then(data => setFileData(data));
};

export const checkForPendingAgsnsOrAsn = (
  rowsData,
  selectedRows,
  selectedDate,
  asnOrAgsntatus,
) => {
  const allPlannedAgsnsOrAsn = rowsData.filter(
    agsnOrAsn => agsnOrAsn.status === asnOrAgsntatus,
  );
  if (moment(selectedDate).isSameOrBefore(moment())) {
    return false;
  }
  if (allPlannedAgsnsOrAsn.length && selectedRows.length) {
    const valid = selectedRows.every(
      (selectedRow, index) => selectedRow.id === allPlannedAgsnsOrAsn[index].id,
    );
    return valid;
  }
  return false;
};

export const setStylesAccordingToPlant = (
  plant,
  setStyles,
  defaultStyles,
  jlrStyles,
) => {
  if (JLR_PLANT === plant.label) {
    setStyles(jlrStyles);
  } else {
    setStyles(defaultStyles);
  }
};

export const getoptionalColumnsByBU = () => {
  const buSpecificOptionalColumns =
    config?.APPLICATION_VARIANT === APPLICATION_VARIANTS.IPMS4
      ? optionalColumns
      : optionalColumnsExceptCv;
  return buSpecificOptionalColumns;
};

export const getFilterOptionsByBU = () => {
  const buSpecificFilterOptions =
    config?.APPLICATION_VARIANT === APPLICATION_VARIANTS.IPMS4
      ? filterOptions
      : filterOptionsForPV;
  return buSpecificFilterOptions;
};

export function getDatesRange(startDate, stopDate) {
  var dateArray = [];
  var currentDate = moment(startDate);
  var stopDate = moment(stopDate);
  while (currentDate <= stopDate) {
    dateArray.push(moment(currentDate).format('YYYY-MM-DD'));
    currentDate = moment(currentDate).add(1, 'days');
  }
  return dateArray;
}

function getMonthRange(startDate, endDate) {
  const months = [];
  let currentMonth = startDate.clone();
  while (currentMonth.isSameOrBefore(endDate)) {
    months.push(currentMonth.format('YYYY-MM'));
    currentMonth.add(1, 'months');
  }
  return months;
}

export async function exportToExcel(applicationVariant,intervalType) { 
  const loadData = async (url, params) => {
      try {
          const response = await API.get(url, {
              params: params,
          });
          return response.data; 
      } catch (error) {
          console.error('Error loading data:', error);
          throw error;
      }
  };
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Sheet 1');
  const worksheet2 = workbook.addWorksheet('Sheet 2');
  
  const countriesArray = await loadData(API_RESOURCE_URLS.COUNTRIES);
  const countryCodes = countriesArray.map(item => [item.code + ' - ' + item.country]);
  const coloursArray = await  loadData(API_RESOURCE_URLS.COLOURS);
  const colourCodes = coloursArray.map(item => [item.colourDescription+' ('+item.colourCode+')']);

  for (let rowIndex = 0; rowIndex < countryCodes.length; rowIndex++) {
    const cell = worksheet2.getCell(`B${rowIndex + 2}`);
    cell.value = countryCodes[rowIndex][0];
  }
 
  for (let rowIndex = 0; rowIndex < colourCodes.length; rowIndex++) {
    const cell = worksheet2.getCell(`A${rowIndex + 2}`);
    cell.value = colourCodes[rowIndex][0];
  }
  let currentMonthHeaders = [];
  if (intervalType === INTERVAL_TYPE.MONTH) {
    currentMonthHeaders = getMonthRange(moment(), moment().add(11, 'months')); // Month-wise (for 12 months)
  } else {
    currentMonthHeaders = getDatesRange(moment(), moment().add(29, 'days')); // Day-wise (for 30 days)
  }
  let headerRow = null;
  if (applicationVariant === APPLICATION_VARIANTS.IPMS4) {
    if (intervalType===INTERVAL_TYPE.MONTH) {
      headerRow = worksheet.addRow(mes4HeaderForExcel.concat(currentMonthHeaders));
    } 
    else
    headerRow = worksheet.addRow(headerForExcel.concat(currentMonthHeaders));
  } else {
    headerRow = worksheet.addRow(
      mes4HeaderForExcel.concat(currentMonthHeaders),
    );
  }
  headerRow.eachCell((cell, number) => {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF808080' },
      bgColor: { argb: 'FF0000FF' },
    };
    cell.font = {
      size: 8,
      bold: true,
      name: 'Arial',
      align: 'center',
    };
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    };
  });

  worksheet.getColumn(3).width = 20;
  worksheet.getColumn(13).width = 15;

  let validationOptions;
  if (applicationVariant === APPLICATION_VARIANTS.IPMS4) {
    if (intervalType===INTERVAL_TYPE.MONTH) {
      validationOptions = {
        A: '"Pune,Jamshedpur,Lucknow,Pantnagar,Dharwad,Lucknow-Prolife"',
        B: '"D3,E9,H3,J13,L1,L2,L3,L4,1A,1B,1C,1D,Engine_L1"',
      };
    } 
    else
    validationOptions = {
      A: '"Pune,Jamshedpur,Lucknow,Pantnagar,Dharwad,Lucknow-Prolife"',
      B: '"D3,E9,H3,J13,L1,L2,L3,L4,1A,1B,1C,1D,Engine_L1"',
      D: '"MTO,MTS"',
      E: '"D,E"',
      F: '"yes,no"',
      G: `'Sheet 2'!$B$2:$B${countryCodes.length+1}`,
      H: '"yes,no"',
      I:
        '"A66860,B02930,B64800,B69040,B80080,C60971,C60973,C64650,G64310,H80882,H80885,I64660,J80250,K60901,K64721,S33440,S53860,S79220,S79224,S88882,T66242,T66244,T72350,U64390,T66471"',
      J: '"Bosch,Rane,Robort,Wabco,ZF"',
      L: `'Sheet 2'!$A$2:$A${colourCodes.length+1}`,
    };
  } else if (applicationVariant === APPLICATION_VARIANTS.MES4) {
    validationOptions = {
      A: '"Pune JLR"',
      B: '"L1"',
    };
  } else {
    validationOptions = {
      A: '"Sanand2"',
      B: '"TCF1"',
    };
  }

  Object.keys(validationOptions).forEach(column => {
    for (let rowIndex = 2; rowIndex <= 100; rowIndex++) {
      const cell = worksheet.getCell(column + rowIndex);
      cell.dataValidation = {
        type: 'list',
        allowBlank: false,
        formulae: [validationOptions[column]],
        showDropDown: true,
      };
    }
  }); // for generating upto 100 rows for data validations
  worksheet2.state = 'veryHidden';

  if (applicationVariant === APPLICATION_VARIANTS.IPMS4)
    worksheet.getColumn('K').numFmt = '@'; // for making Build number Text field

  return workbook.xlsx.writeBuffer().then(data => {
    const blob = new Blob([data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);
    return url;
  });
}

export function exportToExcelAgsn() {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Sheet 1');
  const worksheet2 = workbook.addWorksheet('Sheet 2');

  const currentMonthHeaders = getDatesRange(moment(), moment().add(29, 'days'));
  let headerRow = null;
  
    headerRow = worksheet.addRow(headerForAgsnExcel.concat(currentMonthHeaders));
  
  headerRow.eachCell((cell, number) => {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF808080' },
      bgColor: { argb: 'FF0000FF' },
    };
    cell.font = {
      size: 8,
      bold: true,
      name: 'Arial',
      align: 'center',
    };
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    };
  });

  worksheet.getColumn(1).width = 15;
  worksheet.getColumn(2).width = 15;

  worksheet2.state = 'veryHidden';

  for (let rowIndex = 0; rowIndex < sheet2Data.length; rowIndex++) {
    const cell = worksheet2.getCell(`A${rowIndex + 2}`);
    cell.value = sheet2Data[rowIndex][0];
  }

  let validationOptions;

    validationOptions = {
      B: '"Pune,Jamshedpur,Lucknow,Pantnagar,Dharwad,CKD,Spare,VFJ"',
    };

  Object.keys(validationOptions).forEach(column => {
    for (let rowIndex = 2; rowIndex <= 100; rowIndex++) {
      const cell = worksheet.getCell(column + rowIndex);
      cell.dataValidation = {
        type: 'list',
        allowBlank: false,
        formulae: [validationOptions[column]],
        showDropDown: true,
      };
    }
  }); // for generating upto 100 rows for data validations

  return workbook.xlsx.writeBuffer().then(data => {
    const blob = new Blob([data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);
    return url;
  });
}

export async function exportToExcelCabAgsn() {

  const loadData = async (url, params) => {
    try {
        const response = await API.get(url, {
            params: params,
        });
        return response.data; 
    } catch (error) {
        console.error('Error loading data:', error);
        throw error;
    }
};
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Sheet 1');
  const worksheet2 = workbook.addWorksheet('Sheet 2');

  const currentMonthHeaders = getDatesRange(moment(), moment().add(29, 'days'));
  let headerRow = null;
  
    headerRow = worksheet.addRow(headerForCabAgsnExcel.concat(currentMonthHeaders));
  
  headerRow.eachCell((cell, number) => {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF808080' },
      bgColor: { argb: 'FF0000FF' },
    };
    cell.font = {
      size: 8,
      bold: true,
      name: 'Arial',
      align: 'center',
    };
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    };
  });

  worksheet.getColumn(1).width = 15;
  worksheet.getColumn(2).width = 15;

  worksheet2.state = 'veryHidden';

  for (let rowIndex = 0; rowIndex < sheet2Data.length; rowIndex++) {
    const cell = worksheet2.getCell(`A${rowIndex + 2}`);
    cell.value = sheet2Data[rowIndex][0];
  }

  const countriesArray = await loadData(API_RESOURCE_URLS.COUNTRIES);
  const countryCodes = countriesArray.map(item => [item.code]);

  const coloursArray = await loadData(API_RESOURCE_URLS.COLOURS);
  const colourCodes = coloursArray.map(item => [item.colourCode]);

  for (let rowIndex = 0; rowIndex < countryCodes.length; rowIndex++) {
    const cell = worksheet2.getCell(`B${rowIndex + 2}`);
    cell.value = countryCodes[rowIndex][0];
  }

  for (let rowIndex = 0; rowIndex < colourCodes.length; rowIndex++) {
    const cell = worksheet2.getCell(`A${rowIndex + 2}`);
    cell.value = colourCodes[rowIndex][0];
  }

  let validationOptions;

    validationOptions = {
      B: '"Pune,Jamshedpur,Lucknow,Pantnagar,Dharwad,CKD,Spare,VFJ"',
      C: `'Sheet 2'!$A$2:$A${colourCodes.length+1}`,
      D: `'Sheet 2'!$B$2:$B${countryCodes.length+1}`,
    };

  Object.keys(validationOptions).forEach(column => {
    for (let rowIndex = 2; rowIndex <= 100; rowIndex++) {
      const cell = worksheet.getCell(column + rowIndex);
      cell.dataValidation = {
        type: 'list',
        allowBlank: false,
        formulae: [validationOptions[column]],
        showDropDown: true,
      };
    }
  }); // for generating upto 100 rows for data validations

  return workbook.xlsx.writeBuffer().then(data => {
    const blob = new Blob([data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);
    return url;
  });
}

export async function exportToExcelEvrNumber() {

  const loadData = async (url, params) => {
    try {
        const response = await API.get(url, {
            params: params,
        });
        return response.data; 
    } catch (error) {
        console.error('Error loading data:', error);
        throw error;
    }
};
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Sheet 1');
  const worksheet2 = workbook.addWorksheet('Sheet 2');

  let headerRow = null;
  
    headerRow = worksheet.addRow(headerForEvrExcel);
  
  headerRow.eachCell((cell, number) => {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF808080' },
      bgColor: { argb: 'FF0000FF' },
    };
    cell.font = {
      size: 8,
      bold: true,
      name: 'Arial',
      align: 'center',
    };
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    };
  });

  worksheet.getColumn(1).width = 15;
  worksheet.getColumn(2).width = 15;
  worksheet.getColumn(3).width = 15;
  worksheet.getColumn(5).width = 20;
  worksheet.getColumn(6).width = 20;
  worksheet.getColumn(7).width = 20;


  worksheet.getColumn(5).numFmt = 'yyyy-mm-dd'; // Format column 5 as date (Valid From)
  worksheet.getColumn(6).numFmt = 'yyyy-mm-dd'; // Format column 6 as date (Valid To)


  worksheet2.state = 'veryHidden';

  for (let rowIndex = 0; rowIndex < sheet2Data.length; rowIndex++) {
    const cell = worksheet2.getCell(`A${rowIndex + 2}`);
    cell.value = sheet2Data[rowIndex][0];
  }

  const countriesArray = await loadData(API_RESOURCE_URLS.COUNTRIES);
  const countryCodes = countriesArray.map(item => `${item.code} - ${item.country}`) 

  const coloursArray = await  loadData(API_RESOURCE_URLS.COLOURS);
  const colourCodes = coloursArray.map(item => `${item.colourCode} - ${item.colourDescription}`);

  for (let rowIndex = 0; rowIndex < countryCodes.length; rowIndex++) {
    const cell = worksheet2.getCell(`B${rowIndex + 2}`);
    cell.value = countryCodes[rowIndex];
  }

  for (let rowIndex = 0; rowIndex < colourCodes.length; rowIndex++) {
    const cell = worksheet2.getCell(`A${rowIndex + 2}`);
    cell.value = colourCodes[rowIndex];
  }

  let validationOptions;

    validationOptions = {
      A: '"Pune,Jamshedpur,Lucknow,Pantnagar,Dharwad"',
      G: `'Sheet 2'!$B$2:$B${countryCodes.length+1}`,
      H: `'Sheet 2'!$A$2:$A${colourCodes.length+1}`,
    };

  Object.keys(validationOptions).forEach(column => {
    for (let rowIndex = 2; rowIndex <= 100; rowIndex++) {
      const cell = worksheet.getCell(column + rowIndex);
      cell.dataValidation = {
        type: 'list',
        allowBlank: false,
        formulae: [validationOptions[column]],
        showDropDown: true,
      };
    }
  }); // for generating upto 100 rows for data validations

  return workbook.xlsx.writeBuffer().then(data => {
    const blob = new Blob([data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);
    return url;
  });
}

export const shouldIncludeColourAndCountry = (aggregate) => {
  if (aggregate === AGGREGATES.CAB || aggregate === AGGREGATES.PAINT) {
    return true;
  }
  if (aggregate === AGGREGATES.BIW && APPLICATION_VARIANT === APPLICATION_VARIANTS.IPMS4) {
    return true;
  }
  return false;
};

